/**
 * Cell selection for the SQL results grid (backlog #80), modelled on Snowflake's results grid: a click
 * selects a cell, the arrow keys move it, Shift (keys or click) and a mouse drag select a rectangle, and
 * Ctrl/⌘+C copies the rectangle so it pastes into Excel or Google Sheets as cells.
 *
 * Rows are POSITIONS in the shown order (after filter and sort), not Arrow row indexes.
 */

export interface GridCell {
  row: number;
  col: number;
}

/** The cell the selection started from (anchor) and the one it extends to (focus, the active cell). */
export interface GridSelection {
  anchor: GridCell;
  focus: GridCell;
}

export interface GridRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export const selectionRect = ({ anchor, focus }: GridSelection): GridRect => ({
  top: Math.min(anchor.row, focus.row),
  bottom: Math.max(anchor.row, focus.row),
  left: Math.min(anchor.col, focus.col),
  right: Math.max(anchor.col, focus.col),
});

export const rectContains = (rect: GridRect, row: number, col: number): boolean =>
  row >= rect.top && row <= rect.bottom && col >= rect.left && col <= rect.right;

const clamp = (value: number, max: number) => Math.max(0, Math.min(max, value));

/**
 * Where a navigation key moves the active cell, or null when the key doesn't navigate.
 * Arrows move one cell (Ctrl/⌘ jumps to that edge), Home/End go to the row's first/last column
 * (Ctrl/⌘: the first/last cell of the grid), PageUp/PageDown move a screenful of rows.
 */
export const moveFocus = ({ key, ctrlOrMeta, focus, rowCount, colCount, pageRows }: {
  key: string;
  ctrlOrMeta: boolean;
  focus: GridCell;
  rowCount: number;
  colCount: number;
  pageRows: number;
}): GridCell | null => {
  const lastRow = rowCount - 1;
  const lastCol = colCount - 1;
  switch (key) {
    case 'ArrowUp': return { row: ctrlOrMeta ? 0 : clamp(focus.row - 1, lastRow), col: focus.col };
    case 'ArrowDown': return { row: ctrlOrMeta ? lastRow : clamp(focus.row + 1, lastRow), col: focus.col };
    case 'ArrowLeft': return { row: focus.row, col: ctrlOrMeta ? 0 : clamp(focus.col - 1, lastCol) };
    case 'ArrowRight': return { row: focus.row, col: ctrlOrMeta ? lastCol : clamp(focus.col + 1, lastCol) };
    case 'Home': return ctrlOrMeta ? { row: 0, col: 0 } : { row: focus.row, col: 0 };
    case 'End': return ctrlOrMeta ? { row: lastRow, col: lastCol } : { row: focus.row, col: lastCol };
    case 'PageUp': return { row: clamp(focus.row - pageRows, lastRow), col: focus.col };
    case 'PageDown': return { row: clamp(focus.row + pageRows, lastRow), col: focus.col };
    default: return null;
  }
};

/** A cell for the clipboard: its full text, or null for SQL NULL (pasted as an empty cell). */
export type ClipboardValue = string | null;

const tsvField = (value: ClipboardValue): string => {
  if (value === null) return '';
  return /[\t\n\r"]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
};

/**
 * Plain-text clipboard form. One cell is its value as is (so it pastes cleanly anywhere); a range is
 * tab-separated columns and newline-separated rows, with a value holding a tab, line break or quote
 * quoted the way Excel and Google Sheets read it back into one cell.
 */
export const toClipboardText = (values: ClipboardValue[][]): string => {
  if (values.length === 1 && values[0].length === 1) return values[0][0] ?? '';
  return values.map((row) => row.map(tsvField).join('\t')).join('\n');
};

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * HTML clipboard form, which Excel prefers over the text: one `<td>` per cell, so a value with line breaks
 * stays in one cell, and text columns are marked as text so values like `00123` keep their leading zeros.
 */
export const toClipboardHtml = (values: ClipboardValue[][], textColumns: readonly boolean[]): string => {
  const rows = values.map((row) => `<tr>${row.map((value, c) => {
    const style = textColumns[c] ? ' style="mso-number-format:\'\\@\'"' : '';
    const body = value === null ? ''
      : escapeHtml(value).replace(/\r?\n/g, '<br style="mso-data-placement:same-cell">');
    return `<td${style}>${body}</td>`;
  }).join('')}</tr>`);
  return `<meta charset="utf-8"><table><tbody>${rows.join('')}</tbody></table>`;
};

/**
 * Copy through the browser's own copy command: select a scratch element, run `copy`, and fill the copy event.
 * It's synchronous inside the key or click handler, which is what Safari requires: Safari refuses
 * `navigator.clipboard.write` from a Cmd+C handler ("The request is not allowed by the user agent", 2026-10-14).
 * The listener sits on `window` in the capture phase and stops the event there, so no other copy handler on
 * the page (the SQL editor's Monaco) can replace what we put on the clipboard.
 */
export const copyWithCopyCommand = (plain: string, html?: string): boolean => {
  const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const scratch = document.createElement('span');
  scratch.textContent = '\u00a0';
  scratch.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;'
    + 'user-select:text;-webkit-user-select:text;white-space:pre';
  document.body.appendChild(scratch);
  const selection = document.getSelection();
  let written = false;
  const onCopy = (e: ClipboardEvent) => {
    if (!e.clipboardData) return;
    e.clipboardData.setData('text/plain', plain);
    if (html !== undefined) e.clipboardData.setData('text/html', html);
    e.preventDefault();
    e.stopImmediatePropagation();
    written = true;
  };
  window.addEventListener('copy', onCopy, true);
  try {
    const range = document.createRange();
    range.selectNodeContents(scratch);
    selection?.removeAllRanges();
    selection?.addRange(range);
    document.execCommand('copy');
  } catch {
    written = false;
  } finally {
    window.removeEventListener('copy', onCopy, true);
    selection?.removeAllRanges();
    scratch.remove();
    previousFocus?.focus({ preventScroll: true });
  }
  return written;
};

/**
 * Put text and HTML on the clipboard from inside a key or click handler: the copy command first (every
 * browser, including Safari and plain HTTP), the async Clipboard API only if that didn't take.
 */
export const writeClipboard = async (plain: string, html: string): Promise<void> => {
  if (copyWithCopyCommand(plain, html)) return;
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    await navigator.clipboard.write([new ClipboardItem({
      'text/plain': new Blob([plain], { type: 'text/plain' }),
      'text/html': new Blob([html], { type: 'text/html' }),
    })]);
    return;
  }
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(plain);
    return;
  }
  throw new Error('This browser blocked access to the clipboard.');
};
